import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminFetch } from '@/lib/apiClient';
import { idempotencyKeyFor } from '@/lib/http-core';
import type {
  Policy,
  PolicyCreatePayload,
  PolicyListQuery,
  PolicyListResult,
  PolicyPagination,
  PolicyType,
  PolicyUpdatePayload,
} from '../types/policy';

const BASE = '/admin/v1/policies';

/** be-catalog 계약: admin write 는 Idempotency-Key 헤더 필요. */
const writeHeaders = (method: string, path: string, body: unknown): HeadersInit => ({
  'Idempotency-Key': idempotencyKeyFor(method, path, body),
});

interface ListEnvelope {
  success: boolean;
  data?: Policy[] | null;
  meta?: { pagination?: PolicyPagination } | null;
}

interface SingleEnvelope {
  success: boolean;
  data?: Policy | null;
}

export const policiesQueryKey = (query: PolicyListQuery) =>
  ['admin', 'policies', query.page, query.size, query.type ?? '', query.active ?? ''] as const;

export const policyQueryKey = (policyId: number) => ['admin', 'policy', policyId] as const;

/**
 * BE pagination contract (per BUG-008): the request `page` is Spring Pageable
 * 0-based while the envelope `meta.pagination.page` comes back 1-based. We
 * convert the UI's 1-based page to 0-based here; the response page is consumed
 * as-is by the Pagination primitive (which is 1-based).
 */
const buildSearchParams = (query: PolicyListQuery): string => {
  const params = new URLSearchParams({
    page: String(Math.max(0, query.page - 1)),
    size: String(query.size),
    sort: 'createdAt,desc',
  });
  if (query.type) params.set('type', query.type);
  if (query.active !== undefined) params.set('active', String(query.active));
  return params.toString();
};

const fetchPolicies = async (query: PolicyListQuery): Promise<PolicyListResult> => {
  const res = await adminFetch(`${BASE}?${buildSearchParams(query)}`);
  if (!res.ok) throw new Error(`policies list failed: ${res.status}`);
  const env = (await res.json()) as ListEnvelope;
  const items = env.data ?? [];
  const pagination: PolicyPagination = env.meta?.pagination ?? {
    page: query.page,
    size: query.size,
    totalItems: items.length,
    totalPages: 1,
  };
  return { items, pagination };
};

const fetchPolicy = async (policyId: number): Promise<Policy> => {
  const res = await adminFetch(`${BASE}/${policyId}`);
  if (!res.ok) throw new Error(`policy fetch failed: ${res.status}`);
  const env = (await res.json()) as SingleEnvelope;
  if (!env.data) throw new Error('no data');
  return env.data;
};

export const usePoliciesQuery = (query: PolicyListQuery) =>
  useQuery<PolicyListResult>({
    queryKey: policiesQueryKey(query),
    queryFn: () => fetchPolicies(query),
  });

export const usePolicyQuery = (policyId: number | null) =>
  useQuery<Policy>({
    queryKey: policyQueryKey(policyId ?? -1),
    queryFn: () => fetchPolicy(policyId as number),
    enabled: policyId != null,
  });

export const usePolicyMutation = () => {
  const qc = useQueryClient();
  const invalidateList = () => qc.invalidateQueries({ queryKey: ['admin', 'policies'] });

  const create = useMutation({
    mutationFn: async (payload: PolicyCreatePayload): Promise<Policy> => {
      const res = await adminFetch(BASE, {
        method: 'POST',
        headers: writeHeaders('POST', BASE, payload),
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`policy create failed: ${res.status}`);
      const env = (await res.json()) as SingleEnvelope;
      if (!env.data) throw new Error('no data');
      return env.data;
    },
    onSuccess: invalidateList,
  });

  const update = useMutation({
    mutationFn: async ({
      policyId,
      payload,
    }: {
      policyId: number;
      payload: PolicyUpdatePayload;
    }): Promise<Policy> => {
      const path = `${BASE}/${policyId}`;
      const res = await adminFetch(path, {
        method: 'PUT',
        headers: writeHeaders('PUT', path, payload),
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`policy update failed: ${res.status}`);
      const env = (await res.json()) as SingleEnvelope;
      if (!env.data) throw new Error('no data');
      return env.data;
    },
    onSuccess: (_, { policyId }) => {
      void qc.invalidateQueries({ queryKey: policyQueryKey(policyId) });
      invalidateList();
    },
  });

  const remove = useMutation({
    mutationFn: async (policyId: number): Promise<void> => {
      const path = `${BASE}/${policyId}`;
      const res = await adminFetch(path, {
        method: 'DELETE',
        headers: writeHeaders('DELETE', path, ''),
      });
      if (!res.ok) throw new Error(`policy delete failed: ${res.status}`);
    },
    onSuccess: invalidateList,
  });

  return { create, update, remove };
};

/**
 * POST /{id}/default — marks a policy as the default for its type and clears
 * the previous default of the same type server-side. Returns the updated
 * PolicyResponse.
 */
export const useSetDefaultPolicyMutation = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (policyId: number): Promise<Policy> => {
      const path = `${BASE}/${policyId}/default`;
      const res = await adminFetch(path, {
        method: 'POST',
        headers: writeHeaders('POST', path, ''),
      });
      if (!res.ok) throw new Error(`policy set-default failed: ${res.status}`);
      const env = (await res.json()) as SingleEnvelope;
      if (!env.data) throw new Error('no data');
      return env.data;
    },
    onSuccess: (policy) => {
      void qc.invalidateQueries({ queryKey: policyQueryKey(policy.id) });
      void qc.invalidateQueries({ queryKey: ['admin', 'policies'] });
    },
  });
};

/** Active policies of a given type, for selector dropdowns. type omitted = all. */
export const useActivePoliciesByType = (type?: PolicyType) =>
  useQuery<PolicyListResult>({
    queryKey: ['admin', 'policies', 'by-type', type ?? ''] as const,
    queryFn: () =>
      fetchPolicies({
        page: 1,
        size: 100,
        active: true,
        ...(type ? { type } : {}),
      }),
  });
